import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  redirectTo?: string;
}

/**
 * Route guard component that enforces role-based access control.
 * Prevents unauthorized users from accessing protected routes.
 */
const ProtectedRoute = ({ 
  children, 
  allowedRoles, 
  redirectTo = '/dashboard' 
}: ProtectedRouteProps) => {
  const { user, loading } = useAuth();

  // Show nothing while loading to prevent flash
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Not logged in - this shouldn't happen as App.tsx handles it, but safety first
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check if user's role is in the allowed roles list
  const hasAccess = allowedRoles.includes(user.role);

  if (!hasAccess) {
    // Log unauthorized access attempt (only in dev)
    if (import.meta.env.DEV) {
      console.warn(`Access denied: User role "${user.role}" not in allowed roles:`, allowedRoles);
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
